import React, { useState, useEffect, useRef } from 'react';
import Loading from "../Common/Loading"

// --- Configuration ---
const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';

// --- Interfaces ---
interface DistributorData {
    SchoolCode: string;
    FamilyCode: string;
    CompanyName: string;
    DLRorHO: string;
    ParentName: string;
    FName: string;
    LName: string;
    Login: string;
    Password?: string;
    Email: string;
    ADDRESS: string;
    Phone: string;
    Mobile: string;
    RegisteredDate: string;
    Expirydate: string;
    ActivityStatus: string;
}

interface FormState {
    txtSchoolCode: string;
    txtLogin: string;
    txtPassword: string;
    txtConfirmPassword: string;
    txtCompanyName: string;
    txtFirstName: string;
    txtLastName: string;
    ddlCountryManager: string;
    ddlHO: string;
    txtAddress1: string;
    ddlStatus: string;
    txtAddress2: string;
    ddlState: string;
    txtCity: string;
    txtPinCode: string;
    txtAreaCode: string;
    txtPhone: string;
    txtMobile: string;
    txtEmail: string;
    chbDateOfBirth: boolean;
    calDOB: string;
    calDOJ: string;
    ddlCurrency: string;
    txtBankAcNo: string;
    txtBankID: string;
    txtBankName: string;
    txtLapTop: string;
    txtHST: string;
    txtPST: string;
    txtGST: string;
    txtCommissionRate: string;
    txtCreditTermTimeframe: string;
    txtCreditTermCredit: string;
}

const EL_RegisterDistributors: React.FC = () => {
    // --- Query Params Logic (ARD) ---
    const queryParams = new URLSearchParams(window.location.search);
    const ARD = queryParams.get('ARD') || 'A'; // 'A' for Add, 'R' for Register/Modify

    // --- State ---
    const [pageTitle, setPageTitle] = useState('');
    const [showGrid, setShowGrid] = useState(false);
    const [showDistControls, setShowDistControls] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    // Dropdown Data
    const [countries, setCountries] = useState<string[]>([]);
    const [states, setStates] = useState<string[]>([]);
    const [headOffices, setHeadOffices] = useState<string[]>([]);
    const [currencies, setCurrencies] = useState<string[]>([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [clicked, setClicked] = useState(false);

    // Grid Data
    const [gridData, setGridData] = useState<DistributorData[]>([]);

    // Form State
    const initialFormState: FormState = {
        txtSchoolCode: '', txtLogin: '', txtPassword: '', txtConfirmPassword: '',
        txtCompanyName: '', txtFirstName: '', txtLastName: '', ddlCountryManager: '',
        ddlHO: '', txtAddress1: '', ddlStatus: 'N', txtAddress2: '', ddlState: '',
        txtCity: '', txtPinCode: '', txtAreaCode: '', txtPhone: '', txtMobile: '',
        txtEmail: '', chbDateOfBirth: false, calDOB: '', calDOJ: new Date().toISOString().split('T')[0],
        ddlCurrency: '', txtBankAcNo: '', txtBankID: '', txtBankName: '', txtLapTop: '',
        txtHST: '', txtPST: '', txtGST: '', txtCommissionRate: '',
        txtCreditTermTimeframe: '', txtCreditTermCredit: ''
    };
    const [formData, setFormData] = useState<FormState>(initialFormState);

    // Hidden State for Logic
    const hidCountry = useRef('');

    // --- Load Initial Data ---
    useEffect(() => {
        if (ARD === 'A') {
            setPageTitle('Register');
            setShowDistControls(true);
            setShowGrid(false);
        } else if (ARD === 'R') {
            setPageTitle('Modify');
            setShowDistControls(false);
            setShowGrid(true);
        }

        const loadData = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/distributors-load-initial/`);

                if (!res.ok) {
                    throw new Error(`HTTP error: ${res.status}`);
                }

                const data = await res.json();

                const countryList: string[] = Array.isArray(data.countries) ? data.countries : [];
                const currencyList: string[] = Array.isArray(data.currencies) ? data.currencies : [];

                setCountries(countryList);
                setCurrencies(currencyList);

                // Select the first currency by default
                if (currencyList.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        ddlCurrency: prev.ddlCurrency || currencyList[0]
                    }));
                }

            } catch (error) {
                console.error("Error loading countries and currencies:", error);
                setCountries([]);
                setCurrencies([]);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [ARD]);

    // --- Helpers ---
    const gfn_Trim = (str: string) => str.trim();
    const gfn_ShowMessage = (msg: string) => alert(msg);

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const day = (`0${d.getDate()}`).slice(-2);
        const month = (`0${d.getMonth() + 1}`).slice(-2);
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatStatus = (status: string) => {
        return status === 'A' ? 'Active' : 'Inactive';
    };

    const fn_IsValidEmail = () => {
        const email = formData.txtEmail;
        if (email.length > 200) {
            gfn_ShowMessage("Please enter the email address with less than 200 characters");
            return false;
        }
        const emails = email.split(';');
        const regexp = /^([0-9a-zA-Z]([-.\w]*[0-9a-zA-Z_])*@([0-9a-zA-Z]([-\w]*[0-9a-zA-Z])?\.)+[a-zA-Z]{2,9})$/;
        for (let i = 0; i < emails.length; i++) {
            if (gfn_Trim(emails[i]) !== "") {
                if (!regexp.test(gfn_Trim(emails[i]))) {
                    alert("Invalid Email ID");
                    return false;
                }
            }
        }
        return true;
    }

    // --- Handlers ---
    const handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const country = e.target.value;
        setSelectedCountry(country);
        hidCountry.current = country;

        setStates([]);
        setHeadOffices([]);

        if (country && country !== '--- Select ---') {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/distributors-get-states/?Country=${country}`);
                const data = await res.json();
                setStates(data.states || []);
                setHeadOffices(data.head_offices || []);
            } catch (err) {
                console.error("Error loading states:", err);
            } finally {
                setLoading(false);
            }
        }
    };

    const fn_Go = async () => {
        if (!selectedCountry || selectedCountry === '--- Select ---') {
            gfn_ShowMessage("Please select a country.");
            return;
        }
        setShowGrid(true);
        setShowDistControls(false);

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/distributors-list/?Country=${selectedCountry}`);
            const data = await res.json();
            setGridData(data.data || []);
        } catch (e) {
            // Fallback for visual testing if API fails
            setGridData([
                { SchoolCode: '1', FamilyCode: 'A', CompanyName: 'AUSVELS', DLRorHO: 'HO', ParentName: '', FName: 'Administrator', LName: '', Login: 'adminshaurya', Password: '123', Email: 'test@test.com', ADDRESS: '123 St', Phone: '1234567', Mobile: '', RegisteredDate: '2016-09-29', Expirydate: '2017-09-29', ActivityStatus: 'N' },
                { SchoolCode: '2', FamilyCode: 'A', CompanyName: 'Kinetic Education', DLRorHO: 'DLR', ParentName: 'AUSVELS', FName: 'Administrator', LName: '', Login: 'admindlr', Password: '123', Email: '', ADDRESS: '', Phone: '', Mobile: '', RegisteredDate: '2014-07-23', Expirydate: '2040-07-12', ActivityStatus: 'A' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { id, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [id]: (e.target as HTMLInputElement).checked }));
        } else {
            setFormData(prev => ({ ...prev, [id]: value }));
        }
    };

    const fn_CheckAvail = async (isSaveClicked: boolean) => {
        if (ARD === 'R') return true;
        if (gfn_Trim(formData.txtSchoolCode) === "") {
            gfn_ShowMessage("Please enter a schoolcode.");
            return false;
        }
        if (gfn_Trim(formData.txtLogin) === "") {
            gfn_ShowMessage("Please enter a Login.");
            return false;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/distributors-check-availability/?CheckSchoolCode=${formData.txtSchoolCode}&CheckLogin=${formData.txtLogin}`);
            const data = await res.json();

            if (data.status === "N_Schoolcode") {
                gfn_ShowMessage("SchoolCode already exists.");
                return false;
            }
            if (data.status === "N_Login") {
                gfn_ShowMessage("Login already exists.");
                return false;
            }

            if (!isSaveClicked) {
                gfn_ShowMessage("Login is available for use.");
            }
            return true;
        } catch (err) {
            console.error(err);
            gfn_ShowMessage("Error checking availability.");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const fn_Validate = () => {
        if (!selectedCountry || selectedCountry === '--- Select ---') {
            gfn_ShowMessage("Please select a country");
            return false;
        }
        if (formData.txtEmail === "") {
            gfn_ShowMessage("Please enter Email.");
            return false;
        }
        if (gfn_Trim(formData.txtSchoolCode) === "") {
            gfn_ShowMessage("Please enter a schoolcode.");
            return false;
        }
        if (formData.txtSchoolCode.indexOf(" ") > 0) {
            gfn_ShowMessage("Please enter schoolcode without space.");
            return false;
        }
        if (gfn_Trim(formData.txtLogin) === "") {
            gfn_ShowMessage("Please enter a Login.");
            return false;
        }
        if (gfn_Trim(formData.txtPassword) === "") {
            gfn_ShowMessage("Please enter Password.");
            return false;
        }
        if (gfn_Trim(formData.txtConfirmPassword) === "") {
            gfn_ShowMessage("Please enter Confirm Password.");
            return false;
        }
        if (gfn_Trim(formData.txtPassword) !== gfn_Trim(formData.txtConfirmPassword)) {
            gfn_ShowMessage("Password mismatch!");
            return false;
        }
        if (gfn_Trim(formData.txtFirstName) === "") {
            gfn_ShowMessage("Please enter the first name");
            return false;
        }
        if (gfn_Trim(formData.txtCompanyName) === "") {
            gfn_ShowMessage("Please enter company name");
            return false;
        }
        // if (formData.ddlHO === '') {
        //     gfn_ShowMessage("Please select a Head Office.");
        //     return false;
        // }
        if (formData.ddlState === '') {
            gfn_ShowMessage("Please select a State");
            return false;
        }
        if (gfn_Trim(formData.txtCity) === "") {
            gfn_ShowMessage("Please select a city.");
            return false;
        }

        if (!fn_IsValidEmail()) return false;

        return true;
    };

    const fn_Save = async () => {
        if (ARD === 'A') {
            const avail = await fn_CheckAvail(true);
            if (!avail) return;
        }
        if (!fn_Validate()) return;

        const payload = {
            ARD: ARD,
            txtSchoolCode: formData.txtSchoolCode,
            txtLogin: formData.txtLogin,
            txtPassword: formData.txtPassword,
            txtCompanyName: formData.txtCompanyName,
            txtFirstName: formData.txtFirstName,
            txtLastName: formData.txtLastName,
            txtAddress1: formData.txtAddress1,
            ddlCountry: selectedCountry,
            txtPhone: formData.txtPhone,
            txtEmail: formData.txtEmail,
            ddlStatus: formData.ddlStatus,
            ddlState: formData.ddlState,
            txtCity: formData.txtCity,
            txtPinCode: formData.txtPinCode,
            txtMobile: formData.txtMobile,
            txtHST: formData.txtHST,
            txtPST: formData.txtPST,
            txtGST: formData.txtGST,
            txtBankAcNo: formData.txtBankAcNo,
            txtBankID: formData.txtBankID,
            txtBankName: formData.txtBankName,
            ddlCountryManager: formData.ddlCountryManager,
            calDOB: formData.calDOB,
            chbDateOfBirth: formData.chbDateOfBirth,
            calDOJ: formData.calDOJ,
            txtLapTop: formData.txtLapTop,
            txtCommissionRate: formData.txtCommissionRate,
            txtCreditTermTimeframe: formData.txtCreditTermTimeframe,
            txtCreditTermCredit: formData.txtCreditTermCredit,
            txtAreaCode: formData.txtAreaCode,
            ddlHO: formData.ddlHO,
            ddlCurrency: formData.ddlCurrency
        };

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/distributors-save/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.message === "Added") {
                gfn_ShowMessage("Distributor is added successfully.");
            } else if (data.message === "Modified") {
                gfn_ShowMessage("Distributor is modified successfully.");
            } else {
                gfn_ShowMessage("Error: " + data.error);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditRow = async (
        schoolCode: string,
        familyCode: string,
        login: string
    ) => {
        const params = new URLSearchParams({
            SchoolCode: schoolCode,
            FamilyCode: familyCode,
            Login: login
        });

        setLoading(true);
        try {
            const res = await fetch(
                `${API_BASE}api/distributors-get-details/?${params.toString()}`
            );

            const data = await res.json();
            const det = data.data;

            if (det) {
                setFormData(prev => ({
                    ...prev,
                    txtSchoolCode: schoolCode,
                    txtLogin: login,
                    txtPassword: det.Password || '',
                    txtConfirmPassword: det.Password || '',
                    txtCompanyName: det.Name || '',
                    txtFirstName: det.FName || '',
                    txtLastName: det.LName || '',
                    txtAddress1: det.Address || '',
                    ddlStatus: det.Status === 'A' ? 'A' : 'N',
                    ddlState: det.State || '',
                    ddlHO: det.ParentSchoolCode || '',
                    txtCity: det.City || '',
                    txtPinCode: det.PostalCode || '',
                    txtAreaCode: det.AreaCode || '',
                    txtPhone: det.PhoneNo || '',
                    txtMobile: det.Mobile || '',
                    txtEmail: det.Email || '',
                    chbDateOfBirth: !!det.DateOfBirth,
                    calDOB: det.DateOfBirth ? det.DateOfBirth.split('T')[0] : '',
                    calDOJ: det.DateOfJoin ? det.DateOfJoin.split('T')[0] : '',
                    ddlCurrency: det.Currency || currencies[0] || '',
                    txtBankAcNo: det.BankAccNo || '',
                    txtBankID: det.BankID || '',
                    txtBankName: det.BankName || '',
                    txtLapTop: det.LapTopDetails || '',
                    txtHST: det.HST || '',
                    txtPST: det.PST || '',
                    txtGST: det.GST || '',
                    txtCommissionRate: det.CommissionRate || '',
                    txtCreditTermTimeframe: det.CreditTermTimeFrame || '',
                    txtCreditTermCredit: det.CreditTermCredit || ''
                }));

                setShowGrid(false);
                setShowDistControls(true);
            }
        } catch (err) {
            console.error(err);
            gfn_ShowMessage("Error loading distributor details.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToMenu = () => {
        window.location.href = '/SysAdmin';
    };

    // Back button on the form:
    //  - Add mode: there is no list to go back to, so return to the menu
    //  - Modify mode: clear the form and return to the (refreshed) list
    const handleBack = () => {
        if (ARD === 'A') {
            handleBackToMenu();
            return;
        }

        setFormData({ ...initialFormState, ddlCurrency: currencies[0] || '' });

        if (selectedCountry) {
            fn_Go(); // shows the grid and reloads the list
        } else {
            setShowDistControls(false);
            setShowGrid(true);
        }
    };

    // --- Styles ---
    const styles = {
        cssButtonHighlight: {
            backgroundColor: 'orange', fontFamily: 'Trebuchet MS', fontSize: '12pt',
            fontWeight: 'bold', color: 'White', border: 'solid 2px #119ECC',
            height: '28px', width: '100px', cursor: 'pointer', padding: 0
        } as React.CSSProperties,
        cssDDL: {
            border: 'solid 1px #003385', backgroundColor: 'white', fontFamily: 'Calibri',
            fontSize: '12pt', color: 'black', width: '209px'
        } as React.CSSProperties,
        cssTextBox: {
            fontFamily: 'Trebuchet MS', fontSize: '11pt', border: 'solid 1px #006600',
            paddingLeft: '5px', width: '200px'
        } as React.CSSProperties,
        container: {
            textAlign: 'center' as const, width: '100%'
        },
        // Grid Specific Styles matching the screenshot
        gridTable: {
            width: "max-content",
            minWidth: "100%",
            borderCollapse: "collapse" as const,
            backgroundColor: "#E0E0FE",
            fontFamily: "Arial",
            fontSize: "10pt",
            border: "1px solid #80a0ff"
        },
        gridHeader: {
            backgroundColor: '#c7ddfd', color: '#2859ac', fontWeight: 'bold',
            textAlign: 'center' as const, border: '1px solid #80a0ff', padding: '8px', position: "sticky" as const,
            top: 0,
            zIndex: 50

        },
        gridRow: {
            backgroundColor: '#f4faff', cursor: 'pointer', height: '22px', padding: "8px"
        },
        gridCell: {
            borderRight: '1px solid #80a0ff', padding: '8px', whiteSpace: 'nowrap' as const,
            color: '#2859ac'
        },
        gridRowAlt: {
            backgroundColor: 'white', cursor: 'pointer', height: '22px'
        }
    };

    return (
        <div
            style={{
                margin: 0,
                backgroundColor: "#e0e0fe",
                minHeight: "100vh",
                width: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {loading && <Loading />}

            <div style={styles.container}>
                {/* Header Section */}
                <div
                    id="div1"
                    style={{
                        color: "Maroon",
                        fontSize: "14pt",
                        padding: "8px 12px",
                        border: "1px solid darkGray",
                        backgroundColor: "#ECFACB",
                        position: "sticky",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        width: "100%",
                        boxSizing: "border-box",
                        flexShrink: 0
                    }}
                >
                    <b>{pageTitle} - Distributor</b>&nbsp;&nbsp;&nbsp;for&nbsp;Country:&nbsp;
                    <span style={{ color: 'Red' }}>*</span>&nbsp;
                    <select
                        id="ddlCountry"
                        style={{ ...styles.cssDDL, width: '150px' }}
                        onChange={handleCountryChange}
                        value={selectedCountry}
                        disabled={ARD === 'R' && showDistControls}
                    >
                        <option value="">--- Select ---</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>

                    {ARD === 'R' && (
                        <span style={{ display: showGrid ? 'inline' : 'none' }}>&nbsp;&nbsp;&nbsp;
                            <button id="btnGo" onClick={fn_Go} style={styles.cssButtonHighlight}>Go</button>
                        </span>
                    )}
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <button id="btnBackToMenu" onClick={handleBackToMenu} style={{ ...styles.cssButtonHighlight, width: '135px' }}>Back To Menu</button>
                </div>

                {/* Distributor Controls (Form) */}
                <div id="divAddDistributor" style={{ textAlign: 'center', padding: '5px', display: showDistControls ? 'block' : 'none', overflowX: 'auto' }}>
                    <table style={{
                        borderBottom: 'solid 2px #80A0FF', borderRight: 'solid 2px #80A0FF',
                        fontFamily: 'Arial', textAlign: 'center', width: "100%",
                        maxWidth: "100%",
                        margin: "10px auto",
                        tableLayout: "auto", marginTop: '10px'
                    }}>
                        <tbody>
                            <tr id="trPersonalDetails">
                                {/* Left Column */}
                                <td style={{ borderLeft: 'solid 2px #80A0FF', borderTop: 'solid 2px #80A0FF', verticalAlign: 'top' }}>
                                    <table cellPadding={5} cellSpacing={0} style={{ border: 'none' }}>
                                        <tbody>
                                            <tr>
                                                <td align="left" style={{ width: '160px', paddingLeft: '10px' }}>School Code:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left" style={{ width: '320px' }}>
                                                    <input type="text" id="txtSchoolCode" maxLength={10} style={styles.cssTextBox} value={formData.txtSchoolCode} onChange={handleInputChange} disabled={ARD === 'R'} />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Login:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left">
                                                    <input type="text" id="txtLogin" maxLength={50} style={{ ...styles.cssTextBox, width: '135px' }} value={formData.txtLogin} onChange={handleInputChange} disabled={ARD === 'R'} />
                                                    {ARD === 'A' && (
                                                        <button onClick={() => fn_CheckAvail(false)} style={{ ...styles.cssButtonHighlight, width: '130px', fontSize: '10pt' }}>Check Availability</button>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Password:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left"><input type="password" id="txtPassword" maxLength={50} style={styles.cssTextBox} value={formData.txtPassword} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Confirm Password:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left"><input type="password" id="txtConfirmPassword" maxLength={50} style={styles.cssTextBox} value={formData.txtConfirmPassword} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Company Name:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left"><input type="text" id="txtCompanyName" maxLength={50} style={styles.cssTextBox} value={formData.txtCompanyName} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>First Name:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left"><input type="text" id="txtFirstName" maxLength={30} style={styles.cssTextBox} value={formData.txtFirstName} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Last Name:</td>
                                                <td align="left"><input type="text" id="txtLastName" maxLength={30} style={styles.cssTextBox} value={formData.txtLastName} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Head Office:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left">
                                                    <select id="ddlHO" style={styles.cssDDL} value={formData.ddlHO} onChange={handleInputChange}>
                                                        <option value="">--- Select ---</option>
                                                        {headOffices.map(ho => <option key={ho} value={ho}>{ho}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Address:</td>
                                                <td align="left"><input type="text" id="txtAddress1" maxLength={80} style={styles.cssTextBox} value={formData.txtAddress1} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Status:</td>
                                                <td align="left">
                                                    <select id="ddlStatus" style={styles.cssDDL} value={formData.ddlStatus} onChange={handleInputChange}>
                                                        <option value="A">Active</option>
                                                        <option value="N">Inactive</option>
                                                    </select>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>StateCode:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left">
                                                    <select id="ddlState" style={styles.cssDDL} value={formData.ddlState} onChange={handleInputChange}>
                                                        <option value="">--- Select ---</option>
                                                        {states.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Suburb:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left"><input type="text" id="txtCity" maxLength={25} style={styles.cssTextBox} value={formData.txtCity} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Zip Code:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left"><input type="text" id="txtPinCode" maxLength={50} style={styles.cssTextBox} value={formData.txtPinCode} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Phone:</td>
                                                <td align="left">
                                                    <input type="text" id="txtAreaCode" maxLength={3} style={{ ...styles.cssTextBox, width: '42px' }} value={formData.txtAreaCode} onChange={handleInputChange} />&nbsp;
                                                    <input type="text" id="txtPhone" maxLength={7} style={{ ...styles.cssTextBox, width: '140px' }} value={formData.txtPhone} onChange={handleInputChange} />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" colSpan={2} style={{ paddingLeft: '10px', textAlign: 'center' }}>or <span style={{ color: 'Red' }}>*</span></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Mobile:</td>
                                                <td align="left"><input type="text" id="txtMobile" maxLength={10} style={styles.cssTextBox} value={formData.txtMobile} onChange={handleInputChange} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Spacer Columns */}
                                <td style={{ width: '1%', borderLeft: 'solid 2px #80A0FF', borderTop: 'solid 2px #80A0FF' }}>&nbsp;</td>
                                <td style={{ width: '1%', borderTop: 'solid 2px #80A0FF' }}>&nbsp;</td>

                                {/* Right Column */}
                                <td style={{ borderTop: 'solid 2px #80A0FF', verticalAlign: 'top' }}>
                                    <table cellPadding={5} cellSpacing={0} width="100%">
                                        <tbody>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>E-Mail:<span style={{ color: 'Red' }}>*</span></td>
                                                <td align="left">
                                                    <textarea id="txtEmail" style={{ ...styles.cssTextBox, overflowX: 'hidden', overflowY: 'auto' }} rows={2} maxLength={200} value={formData.txtEmail} onChange={handleInputChange}></textarea>
                                                    <br /><span style={{ fontSize: '8pt' }}>Enter multiple email with semicolon seperated</span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px', width: '250px' }}>
                                                    <input type="checkbox" id="chbDateOfBirth" checked={formData.chbDateOfBirth} onChange={handleInputChange} /> Date of birth
                                                </td>
                                                <td align="left" style={{ width: '230px', display: formData.chbDateOfBirth ? 'block' : 'none' }}>
                                                    <input type="date" id="calDOB" value={formData.calDOB} onChange={handleInputChange} />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Date Of Join:</td>
                                                <td align="left"><input type="date" id="calDOJ" value={formData.calDOJ} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Target Currency:</td>
                                                <td align="left">
                                                    <select id="ddlCurrency" style={{ ...styles.cssDDL, width: '100px' }} value={formData.ddlCurrency} onChange={handleInputChange}>
                                                        {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Bank AC No:</td>
                                                <td align="left"><input type="text" id="txtBankAcNo" maxLength={100} style={styles.cssTextBox} value={formData.txtBankAcNo} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Bank ID:</td>
                                                <td align="left"><input type="text" id="txtBankID" maxLength={15} style={styles.cssTextBox} value={formData.txtBankID} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Bank Name:</td>
                                                <td align="left"><input type="text" id="txtBankName" maxLength={100} style={styles.cssTextBox} value={formData.txtBankName} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px', verticalAlign: 'top' }}>LapTop Details:</td>
                                                <td align="left"><textarea id="txtLapTop" style={styles.cssTextBox} maxLength={200} value={formData.txtLapTop} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>HST(%):</td>
                                                <td align="left"><input type="text" id="txtHST" maxLength={5} style={styles.cssTextBox} value={formData.txtHST} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>PST(%):</td>
                                                <td align="left"><input type="text" id="txtPST" maxLength={5} style={styles.cssTextBox} value={formData.txtPST} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>GST(%):</td>
                                                <td align="left"><input type="text" id="txtGST" maxLength={5} style={styles.cssTextBox} value={formData.txtGST} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Commission Rate (%):</td>
                                                <td align="left"><input type="text" id="txtCommissionRate" maxLength={12} style={styles.cssTextBox} value={formData.txtCommissionRate} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Credit Term –Timeframe (Days):</td>
                                                <td align="left"><input type="text" id="txtCreditTermTimeframe" maxLength={2} style={styles.cssTextBox} value={formData.txtCreditTermTimeframe} onChange={handleInputChange} /></td>
                                            </tr>
                                            <tr>
                                                <td align="left" style={{ paddingLeft: '10px' }}>Credit Term-Credit :</td>
                                                <td align="left"><input type="text" id="txtCreditTermCredit" maxLength={8} style={styles.cssTextBox} value={formData.txtCreditTermCredit} onChange={handleInputChange} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={5} style={{ padding: '10px', borderLeft: 'solid 2px #80A0FF', borderTop: 'solid 2px #80A0FF' }}>
                                    <button id="btnSave" onClick={fn_Save} style={{ ...styles.cssButtonHighlight, width: '120px' }}>Save</button>&nbsp;&nbsp;
                                    <button id="btnBack" onClick={handleBack} style={styles.cssButtonHighlight}>Back</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Grid View (ResultTable) - EXACT MATCH TO IMAGE */}
                <div
                    id="divShowList"
                    style={{
                        display: showGrid ? "block" : "none",
                        width: "100%",
                        overflow: "auto",
                        padding: 0,
                        boxSizing: "border-box",
                        height: "91vh"
                    }}
                >
                    <table style={styles.gridTable}>
                        <thead>
                            {/* Header Row 1: Merged Headers */}
                            <tr
                                style={{
                                    ...styles.gridHeader,
                                    color: clicked ? "white" : "#2859ac",
                                    backgroundColor: clicked ? "orange" : styles.gridHeader.backgroundColor,
                                    fontSize: "11pt",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s ease, color 0.2s ease",
                                }}
                                onClick={() => setClicked(!clicked)}
                            >
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>#</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Company Name</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>DLR / HO</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Head Office</th>
                                <th colSpan={2} style={{
                                    ...styles.gridCell, background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                    fontWeight: "normal"
                                }}>Name</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Login</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Password</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Email</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Address</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Phone No</th>
                                <th rowSpan={2} style={{
                                    ...styles.gridCell, fontWeight: "normal", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                }}>Mobile</th>
                                <th colSpan={2} style={{
                                    ...styles.gridCell, background: "#c7ddfd",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5,
                                    fontWeight: "normal"
                                }}>Date</th>
                                <th rowSpan={2} style={{ ...styles.gridCell, textAlign: "left", fontWeight: "normal" }}>Activity<br />Status</th>
                            </tr>
                            {/* Header Row 2: Sub Headers */}
                            <tr >
                                <th style={{
                                    ...styles.gridCell, fontWeight: "normal", fontSize: "11pt", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 32, // adjust if needed
                                    zIndex: 4,
                                    borderTop: "1px solid #80a0ff"
                                }}>First</th>
                                <th style={{
                                    ...styles.gridCell, fontWeight: "normal", fontSize: "11pt", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 32, // adjust if needed
                                    zIndex: 4,
                                    borderTop: "1px solid #80a0ff"
                                }}>Last</th>
                                <th style={{
                                    ...styles.gridCell, fontWeight: "normal", fontSize: "11pt", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 32, // adjust if needed
                                    zIndex: 4,
                                    borderTop: "1px solid #80a0ff"
                                }}>Registered</th>
                                <th style={{
                                    ...styles.gridCell, fontWeight: "normal", fontSize: "11pt", background: "#c7ddfd",
                                    position: "sticky",
                                    top: 32, // adjust if needed
                                    zIndex: 4,
                                    borderTop: "1px solid #80a0ff"
                                }}>Expiry</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((row, index) => (
                                <tr
                                    key={`${row.SchoolCode}-${row.FamilyCode}-${row.Login}`}
                                    style={{
                                        ...(index % 2 === 0 ? styles.gridRow : styles.gridRowAlt),
                                        cursor: "pointer",
                                        color: "black"
                                    }}
                                    onClick={() => handleEditRow(
                                        row.SchoolCode,
                                        row.FamilyCode,
                                        row.Login
                                    )}
                                >

                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{index + 1}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.CompanyName}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.DLRorHO}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.ParentName}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.FName}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.LName}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.Login}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.Password}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.Email}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.ADDRESS}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.Phone}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{row.Mobile}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{formatDate(row.RegisteredDate)}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{formatDate(row.Expirydate)}</td>
                                    <td style={{ ...styles.gridCell, color: "black", fontSize: "11pt" }}>{formatStatus(row.ActivityStatus)}</td>
                                </tr>
                            ))}
                            {gridData.length === 0 && (
                                <tr>
                                    <td colSpan={15} style={{ ...styles.gridCell, textAlign: 'center', padding: '10px' }}>
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
};

export default EL_RegisterDistributors;